import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage ProductSelection component state
 * Handles the toggle logic for showing/hiding individual items when a bundle is selected
 */
export const useProductSelectionState = (hasExtrasOutsideBundle) => {
    // State for showing individual items when bundle is selected
    const [showIndividualItems, setShowIndividualItems] = useState(hasExtrasOutsideBundle);
    // Track if user has manually toggled the individual items (to prevent auto-override)
    const [userHasToggled, setUserHasToggled] = useState(false);

    // Auto-expand when user has extras outside bundle (but only if they haven't manually toggled)
    useEffect(() => {
        if (hasExtrasOutsideBundle && !showIndividualItems && !userHasToggled) {
            setShowIndividualItems(true);
        }
        // Important: Don't auto-collapse when hasExtrasOutsideBundle becomes false
        // The user should control the visibility once they've interacted with it
    }, [hasExtrasOutsideBundle, showIndividualItems, userHasToggled]);

    // Reset toggle states when switching between custom purchase and bundle selection
    const resetForMode = useCallback((isCustomPurchase, selectedBundle, hasExtras) => {
        if (isCustomPurchase) {
            // Reset states for custom purchase mode
            setUserHasToggled(false);
            setShowIndividualItems(false); // Not relevant for custom purchase
        } else if (selectedBundle) {
            // Only reset states when selecting a NEW bundle, not when hasExtras changes
            // If user has already toggled, preserve their choice
            if (!userHasToggled) {
                setShowIndividualItems(hasExtras); // Auto-expand if user has extras
            }
            // Don't reset userHasToggled - preserve user's interaction history
        }
    }, [userHasToggled]);

    // Handle manual toggle by user
    const toggleIndividualItems = useCallback(() => {
        setUserHasToggled(true);
        setShowIndividualItems(prev => !prev);
    }, []);

    return {
        // State
        showIndividualItems,
        userHasToggled,
        
        // Actions
        toggleIndividualItems,
        resetForMode,
        
        // Internal state setters (for external control if needed)
        setShowIndividualItems,
        setUserHasToggled
    };
};
